/**
 * Detached-ship kinematics for the travel runtime (plan R17, §2.2): the cruise, tug and lane ticks on R6's laws, the
 * alignment assist, the ETA and the interdiction speed decay. A cruising ship has no Rapier body; it moves
 * kinematically in float64 system-frame metres. The runtime copies the player's `Pos64` in, runs one tick and copies
 * the result out; the trip tests run the same tick on bare layouts.
 *
 * Allocation-free (D-014). The per-tick kernels take only objects and write their results into the ship's fields:
 * V8 boxes a float passed to (or returned from) a call it does not inline, so floats stay in fields between calls.
 */
import { SIM_DT } from '../../core/loop'
import type { Vec3 } from '../../core/math'
import type { LaneRingSite } from '../../galaxy/layout/types'
import type { TravelState } from '../../reach/runtime/types'
import type { TravelConfig } from '../config'
import { cruiseAllowed, lanesAllowed, nearestSurface, stepCruiseSpeed, type MassLockInput } from '../math'

const DEG = Math.PI / 180

/** No nav target: the cruise runs on the free distance and never auto-drops. */
export const TARGET_NONE = 0
/** A point target (relay, POI, lane ring, hulk): the distance to its centre. */
export const TARGET_POINT = 1
/**
 * A sphere target (a body's shell, a field's sphere, a station's hull): the distance to its surface (0 inside). A
 * station is the bounding sphere of radius R (scale build SC-5c), so its distances count from the hull.
 */
export const TARGET_SPHERE = 2

export interface CruiseTarget {
  shape: number
  x: number
  y: number
  z: number
  /** The sphere radius (m); 0 for a point. */
  radius: number
  /**
   * A station target's index in `layout.stations` (its `MassLockInput.stationRadii` slot): `measureShip` leaves it out
   * of the hull check and `targetDropM` drops at `cruise.stationDropFromHullM` off its hull. −1 for anything else.
   */
  station: number
}

export function createCruiseTarget(): CruiseTarget {
  return { shape: TARGET_NONE, x: 0, y: 0, z: 0, radius: 0, station: -1 }
}

/** The distance (m) from p to the target: its centre, or its sphere's surface (0 inside); Infinity with none. */
export function targetDistance(t: CruiseTarget, px: number, py: number, pz: number): number {
  if (t.shape === TARGET_NONE) return Infinity
  const dx = t.x - px
  const dy = t.y - py
  const dz = t.z - pz
  const d = Math.sqrt(dx * dx + dy * dy + dz * dz)
  if (t.shape === TARGET_POINT) return d
  const s = d - t.radius
  return s > 0 ? s : 0
}

/** A detached ship's float64 kinematics, its throttle and the last tick's readings. */
export interface DetachedShip {
  x: number
  y: number
  z: number
  vx: number
  vy: number
  vz: number
  /** m/s along the heading. */
  speed: number
  /** The fraction of the allowed speed flown (the cruise throttle). */
  throttle: number
  /** The law's allowed speed (m/s) this tick. */
  allowed: number
  /** The target distance (Infinity with no target; the signed distance to the exit plane on a lane). */
  dTarget: number
  /** The distance to the nearest shell or non-target station hull (0 inside one). */
  dSurface: number
  /**
   * The cruise and lane spin-up lag τ (s) for the flown hull's band (M10 P5a7a, `cruiseLagSFor`, set by the runtime
   * each tick); below 0: the global `cruise.lagS`. The interdiction decay keeps the global lag.
   */
  lagS: number
}

export function createDetachedShip(): DetachedShip {
  return { x: 0, y: 0, z: 0, vx: 0, vy: 0, vz: 0, speed: 0, throttle: 1, allowed: 0, dTarget: Infinity, dSurface: Infinity, lagS: -1 }
}

/**
 * Writes `ship.dTarget` (to the target's centre or surface; Infinity with none) and `ship.dSurface`: the nearest body
 * or star shell, or the hull (bounding sphere, `stationRadii`) of any station but the target that the ship is closing
 * on (velocity toward its centre; scale build SC-5c), so a cruise line through another station slows on the law and
 * emergency-drops at its hull instead of passing through. A station the ship is leaving does not cap the law, so a
 * departure from one station toward another is not held back by the one behind.
 */
export function measureShip(ship: DetachedShip, target: CruiseTarget, input: MassLockInput): void {
  let d = Infinity
  if (target.shape !== TARGET_NONE) {
    const dx = target.x - ship.x
    const dy = target.y - ship.y
    const dz = target.z - ship.z
    d = Math.sqrt(dx * dx + dy * dy + dz * dz)
    if (target.shape !== TARGET_POINT) d = d > target.radius ? d - target.radius : 0
  }
  ship.dTarget = d
  let s = nearestSurface(ship.x, ship.y, ship.z, input)
  const c = input.stations
  const r = input.stationRadii
  for (let i = 0; i < input.stationCount; i++) {
    if (i === target.station) continue
    const sx = c[i * 3] - ship.x
    const sy = c[i * 3 + 1] - ship.y
    const sz = c[i * 3 + 2] - ship.z
    if (sx * ship.vx + sy * ship.vy + sz * ship.vz < 0) continue
    const h = Math.sqrt(sx * sx + sy * sy + sz * sz) - r[i]
    if (h < s) s = h
  }
  ship.dSurface = s > 0 ? s : 0
}

/** Velocity = heading (hx, hy, hz; unit, read from `h`) × speed; position += velocity × SIM_DT. */
function advance(ship: DetachedShip, h: Vec3): void {
  ship.vx = h.x * ship.speed
  ship.vy = h.y * ship.speed
  ship.vz = h.z * ship.speed
  ship.x += ship.vx * SIM_DT
  ship.y += ship.vy * SIM_DT
  ship.z += ship.vz * SIM_DT
}

/**
 * One cruise tick (SIM_DT). The allowed speed comes from the distances before the move (R6's law); the speed follows
 * `throttle × allowed` (rising through the lag, falling at once, so a step never overshoots the target). The heading is
 * the nose (unit), or exactly the target's direction when the nose is within `alignAssistDeg` of it (written into
 * `heading`, a scratch vector). Moves the ship and refreshes its velocity, `allowed`, `dTarget` and `dSurface`.
 */
export function stepCruiseShip(ship: DetachedShip, nose: Vec3, heading: Vec3, target: CruiseTarget, input: MassLockInput, cfg: TravelConfig): void {
  const c = cfg.cruise
  const hasTarget = target.shape !== TARGET_NONE
  measureShip(ship, target, input)
  ship.allowed = cruiseAllowed(ship.dTarget, ship.dSurface, hasTarget, cfg)
  ship.speed = stepCruiseSpeed(ship.speed, ship.throttle * ship.allowed, SIM_DT, ship.lagS < 0 ? c.lagS : ship.lagS)
  heading.x = nose.x
  heading.y = nose.y
  heading.z = nose.z
  if (hasTarget) {
    const tx = target.x - ship.x
    const ty = target.y - ship.y
    const tz = target.z - ship.z
    const tl = Math.sqrt(tx * tx + ty * ty + tz * tz)
    if (tl > 0 && nose.x * tx + nose.y * ty + nose.z * tz >= tl * Math.cos(c.alignAssistDeg * DEG)) {
      heading.x = tx / tl
      heading.y = ty / tl
      heading.z = tz / tl
    }
  }
  advance(ship, heading)
  measureShip(ship, target, input)
}

/**
 * One tick of flight under an interdiction tug (SIM_DT): the ship keeps flying along its nose while its speed decays
 * (exponentially, both ways, time constant `lagS`) toward `speedFloor × allowed`. Refreshes `allowed`, `dTarget` and
 * `dSurface`.
 */
export function stepTugShip(ship: DetachedShip, nose: Vec3, target: CruiseTarget, input: MassLockInput, cfg: TravelConfig): void {
  const c = cfg.cruise
  measureShip(ship, target, input)
  ship.allowed = cruiseAllowed(ship.dTarget, ship.dSurface, target.shape !== TARGET_NONE, cfg)
  const floor = cfg.interdiction.speedFloor * ship.allowed
  ship.speed = c.lagS > 0 ? floor + (ship.speed - floor) * Math.exp(-SIM_DT / c.lagS) : floor
  advance(ship, nose)
  measureShip(ship, target, input)
}

/**
 * One lane-ride tick (SIM_DT) along `dir` (unit, the direction of travel) toward the plane of `exit`: the lane law on
 * the distance to that plane, through the cruise lag. Writes the signed distance to the plane after the move into
 * `ship.dTarget` (≤ 0 once past it).
 */
export function stepLaneShip(ship: DetachedShip, dir: Vec3, exit: LaneRingSite, cfg: TravelConfig): void {
  const p = exit.position
  const before = (p[0] - ship.x) * dir.x + (p[1] - ship.y) * dir.y + (p[2] - ship.z) * dir.z
  ship.allowed = lanesAllowed(before > 0 ? before : 0, cfg)
  ship.speed = stepCruiseSpeed(ship.speed, ship.throttle * ship.allowed, SIM_DT, ship.lagS < 0 ? cfg.cruise.lagS : ship.lagS)
  advance(ship, dir)
  ship.dTarget = (p[0] - ship.x) * dir.x + (p[1] - ship.y) * dir.y + (p[2] - ship.z) * dir.z
}

/** Exponential approach of `speed` toward `target` with time constant `lagS`, both ways (the interdiction decay). */
export function approachSpeed(speed: number, target: number, dt: number, lagS: number): number {
  if (lagS <= 0) return target
  return target + (speed - target) * Math.exp(-dt / lagS)
}

/**
 * The auto-drop distance (m) to `target`, measured like `dTarget`: `cruise.dropDistanceM` (relays, bodies, fields,
 * POIs), or for a station `cruise.stationDropFromHullM` off its hull (scale build SC-5c). With that key absent a
 * station drops `dropDistanceM` from its centre, as before (0 when the hull reaches past it).
 */
export function targetDropM(target: CruiseTarget, cfg: TravelConfig): number {
  const c = cfg.cruise
  if (target.station < 0) return c.dropDistanceM
  const h = c.stationDropFromHullM
  if (h !== undefined) return h
  return c.dropDistanceM > target.radius ? c.dropDistanceM - target.radius : 0
}

/**
 * Seconds until the auto-drop from `d` metres away at `throttle`, by the cruise law with no shells and no lag:
 * speed = throttle × clamp(k × d, vMin, vMax). 0 at or inside the drop distance `drop` (default
 * `cruise.dropDistanceM`; the runtime passes `targetDropM`); Infinity for no target or no throttle.
 */
export function cruiseEtaS(d: number, throttle: number, cfg: TravelConfig, drop: number = cfg.cruise.dropDistanceM): number {
  const c = cfg.cruise
  if (!(d > drop)) return 0
  if (!(throttle > 0) || d === Infinity) return Infinity
  const dLow = c.vMin / c.k
  const dHigh = c.vMax / c.k
  let t = 0
  const hi1 = d < dLow ? d : dLow
  if (hi1 > drop) t += (hi1 - drop) / c.vMin
  const lo2 = drop > dLow ? drop : dLow
  const hi2 = d < dHigh ? d : dHigh
  if (hi2 > lo2) t += Math.log(hi2 / lo2) / c.k
  const lo3 = drop > dHigh ? drop : dHigh
  if (d > lo3) t += (d - lo3) / c.vMax
  return t / throttle
}

/** `travel.etaS` from `travel.targetDistance` and `travel.throttle`, to `target`'s own drop distance (`targetDropM`). */
export function writeEta(t: TravelState, target: CruiseTarget, cfg: TravelConfig): void {
  t.etaS = cruiseEtaS(t.targetDistance, t.throttle, cfg, targetDropM(target, cfg))
}
